import { TypeOf, z } from "zod";

// NOTE: keep this module free of app/runtime imports (only `zod`). It is
// imported by `wp.ts` (the RTK Query client) but is also unit-tested in
// isolation, and pulling in the api/store layer here re-introduces a
// circular dependency that breaks loading the schema on its own.

const RelatedTicket = z.object({
    id: z.number().positive().int(),
    acf: z.intersection(
        z.object({
            ticket_available_from: z.string(),
            ticket_available_until: z.string(),
            product_behaviour: z.string(),
            team_size: z
                .string()
                .transform((val, ctx) => {
                    if (val === "") {
                        return undefined;
                    }
                    const parsed = Number.parseInt(val, 10);
                    if (isNaN(parsed)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: "Not a number",
                        });

                        return z.NEVER;
                    }
                    return parsed;
                })
                .optional(),
        }),
        z.union([
            z.object({
                eligible_for: z.literal("club_member"),
                eligible_club: z.number().positive().int(),
            }),
            z.object({
                eligible_for: z.union([
                    z.literal("public"),
                    z.literal("alumni"),
                    z.literal("college"),
                    z.literal("staff"),
                    z.literal("student"),
                    z.literal("non_club_member"),
                ]),
                eligible_club: z.literal(false),
            }),
        ])
    ),
    price: z.string(),
    stock_status: z.string(),
    stock_quantity: z.number().nonnegative().int(),
    meta_data: z.array(
        z.object({
            key: z.string(),
            value: z.string(),
        })
    ),
    type: z.string(),
});

const RelatedDto = z.union([RelatedTicket, z.object({}), z.literal(false)]);

export const PageDto = z
    .object({
        ID: z.number().positive().int(),
        acf: z.object({
            event_products: z.array(
                z.object({
                    product: z.object({ ID: z.number() }),
                    // When true, this ticket is limited to one per account
                    // (CRM `auts_oneticketperperson`). Used to cap the quantity
                    // selector at 1; the server enforces the per-account rule.
                    one_ticket_per_person: z.boolean().optional(),
                })
            ),
            event_ticket_comment_label: z.string().optional(),
            ticket_additional_questions: z.array(z.string()).optional(),
        }),
        related: z.record(RelatedDto),
    })
    .transform((value, ctx) => {
        const isTicket = (
            relatedItem: TypeOf<typeof RelatedDto>
        ): relatedItem is TypeOf<typeof RelatedTicket> => {
            return (relatedItem as TypeOf<typeof RelatedTicket>)?.id != null;
        };
        const relatedTickets = Object.values(value.related).filter(isTicket);

        return {
            ...value,
            acf: {
                ...value.acf,
                event_products: value.acf.event_products.map(eventProduct => {
                    const relatedData = relatedTickets.find(
                        ticket => ticket.id === eventProduct.product.ID
                    );
                    if (relatedData == null) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: `Missing related data for event_product(${eventProduct.product.ID})`,
                        });
                        return z.NEVER;
                    }
                    return {
                        ...eventProduct,
                        ...relatedData,
                    };
                }),
            },
        };
    });
